/**
 * Access control — the policy.
 *
 * One table, `POLICY`, maps every `Capability` to a pure predicate over an
 * `AccessSubject`. No React, no hooks, no imports from the app: this file is a
 * function of its inputs and can be reasoned about (and tested) on its own.
 *
 * Two rules for anyone editing it:
 *
 *   1. **Gates fail CLOSED.** Every rule reads a positive signal. A subject
 *      whose queries have not resolved yet has `role: undefined`,
 *      `planFeatures: {}` and `hasSquare: false`, so every rule returns false
 *      and the guarded thing stays hidden until we actually know. The
 *      alternative — showing it and retracting it — leaks the thing being
 *      guarded and flickers. This matches the existing `hasSquare` /
 *      `dataProviders` convention in the nav config and `useMenu`'s
 *      `if (!role) return []`.
 *
 *   2. **Behaviour-preserving ports are marked.** The role tables below are
 *      transcriptions of logic that already shipped. Where a rule says
 *      "ported from", the shape is deliberately identical to the original —
 *      including its oddities — so switching a call site to `can()` cannot
 *      change what anyone sees. Tidy the semantics in a separate, deliberate
 *      change, not as a side effect of centralising them.
 */
import type {
  AccessSubject,
  Capability,
  RoleKey,
  SectionKey,
} from './access.types'

/* ────────────────────────────────────────────────────────────────────────────
 *  Role hierarchy
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Which roles satisfy a given role requirement.
 *
 * Ported verbatim from `useHasUserRole` (`src/state/auth/auth.hooks.ts`), whose
 * ten-case switch this replaces. Note it is deliberately NOT a linear ladder:
 * `it-support` is granted only by super/reseller/admin (never by moderator or
 * GM), and `reports` grants `marketeer` without granting `gm`. Any attempt to
 * flatten this into a numeric level changes who sees what, so it stays a map.
 */
const SATISFIED_BY: Record<RoleKey, readonly RoleKey[]> = {
  super: ['super'],
  reseller: ['super', 'reseller'],
  admin: ['super', 'reseller', 'admin'],
  moderator: ['super', 'reseller', 'admin', 'moderator'],
  gm: ['super', 'reseller', 'admin', 'moderator', 'gm'],
  reports: ['super', 'reseller', 'admin', 'moderator', 'reports'],
  marketeer: [
    'super',
    'reseller',
    'admin',
    'moderator',
    'reports',
    'marketeer',
  ],
  booking: [
    'super',
    'reseller',
    'admin',
    'moderator',
    'gm',
    'reports',
    'marketeer',
    'booking',
  ],
  'front-of-house': [
    'super',
    'reseller',
    'admin',
    'moderator',
    'gm',
    'reports',
    'marketeer',
    'booking',
    'front-of-house',
  ],
  'it-support': ['super', 'reseller', 'admin', 'it-support'],
}

/**
 * Sections each role may NOT reach.
 *
 * Ported verbatim from `denyByKey` in `src/pages/[org_id]/menu-hook.ts`, which
 * until now was the only comprehensive role-to-feature map in the codebase —
 * and which only ever fed the OLD sidebar. Wiring the v4 nav to this same table
 * is what restores role gating on the new UI.
 *
 * `admin` is denied to every role including `admin` itself; only `super`
 * reaches it, and `internal.admin-console` narrows it further still.
 */
const SECTION_DENY_BY_ROLE: Record<RoleKey, readonly SectionKey[]> = {
  super: [],
  reseller: ['admin'],
  admin: ['admin'],
  moderator: ['gift-cards', 'admin'],
  gm: ['marketing', 'wi-fi', 'reports', 'gift-cards', 'brand', 'admin'],
  reports: [
    'marketing',
    'gift-cards',
    'inbox',
    'venues',
    'reviews',
    'loyalty',
    'brand',
    'admin',
  ],
  marketeer: ['bookings', 'inbox', 'gift-cards', 'admin'],
  booking: ['marketing', 'wi-fi', 'reports', 'gift-cards', 'brand', 'admin'],
  'front-of-house': [
    'admin',
    'gift-cards',
    'venues',
    'reports',
    'wi-fi',
    'marketing',
    'brand',
    'loyalty',
    'reviews',
    'inbox',
  ],
  'it-support': [
    'admin',
    'gift-cards',
    'bookings',
    'loyalty',
    'brand',
    'reviews',
    'inbox',
    'marketing',
    'reports',
    'venues',
  ],
}

/**
 * Roles that get the cut-down operational shell: no org settings, no org
 * switcher, no venue creation, a different home page. Ported from the
 * `['front-of-house', 'it-support']` array inlined in `sidebar.tsx`,
 * `profile-nav.tsx` and `[org_id]/index.tsx`.
 */
const OPERATIONAL_ROLES: readonly RoleKey[] = ['front-of-house', 'it-support']

/** Does this subject's role satisfy `required`, per the ported hierarchy? */
export function roleSatisfies(
  subject: AccessSubject,
  required: RoleKey
): boolean {
  if (!subject.role) return false
  return SATISFIED_BY[required].includes(subject.role)
}

/** Is this subject on one of the cut-down operational roles? */
export function isOperationalRole(subject: AccessSubject): boolean {
  if (!subject.role) return false
  return OPERATIONAL_ROLES.includes(subject.role)
}

/* ────────────────────────────────────────────────────────────────────────────
 *  Predicate combinators
 * ──────────────────────────────────────────────────────────────────────────── */

type Rule = (subject: AccessSubject) => boolean

/** Role satisfies `required` (uses the ported hierarchy, not a numeric level). */
const atLeast =
  (required: RoleKey): Rule =>
  (s) =>
    roleSatisfies(s, required)

/** Role is one of `keys`. Use for genuinely flat sets, not hierarchies. */
const roleIn =
  (...keys: RoleKey[]): Rule =>
  (s) =>
    Boolean(s.role && keys.includes(s.role))

/**
 * Role is NOT one of `keys`. Still requires a resolved role, so it fails closed
 * during load rather than granting to everyone whose role has not arrived.
 */
const roleNotIn =
  (...keys: RoleKey[]): Rule =>
  (s) => {
    // Narrowed via a local rather than a cast or a `!` — both are banned, and
    // the early return is what makes the fail-closed behaviour explicit.
    const role = s.role
    if (!role) return false
    return !keys.includes(role)
  }

/** The section is not on this role's deny list. Fails closed while role loads. */
const section =
  (key: SectionKey): Rule =>
  (s) => {
    if (!s.role) return false
    return !SECTION_DENY_BY_ROLE[s.role].includes(key)
  }

/**
 * Any org role at all. For surfaces everyone is meant to see: it grants every
 * role and refuses a subject with no role, so nothing renders before the
 * subject resolves. Not a substitute for a real gate on anything sensitive.
 */
const anyRole: Rule = (s) => Boolean(s.role)

/** The org's subscription includes `feature`. */
const plan =
  (feature: string): Rule =>
  (s) =>
    Boolean(s.planFeatures[feature])

/**
 * The org's subscription includes `feature`, or the `plus` tier that
 * supersedes it. Ported from the fallthrough hardcoded in
 * `useNeedsSubscription` (marketing_plus covers marketing, and so on).
 */
const planOrPlus =
  (feature: string, plusFeature: string): Rule =>
  (s) =>
    Boolean(s.planFeatures[feature] ?? false) ||
    Boolean(s.planFeatures[plusFeature] ?? false)

/** The org's backend supports a given data provider id. */
const provider =
  (id: string): Rule =>
  (s) =>
    s.dataProviders.includes(id)

/** Internal Stampede staff, by email domain. */
const staff: Rule = (s) => s.isStaff

/** Owner of the selected organisation, regardless of org role. */
const orgOwner: Rule = (s) => s.isOrgOwner

/** The selected organisation is one of `ids`. A rollout switch, not a role. */
const orgIn =
  (...ids: readonly string[]): Rule =>
  (s) =>
    s.orgId !== undefined && ids.includes(s.orgId)

/** Named users, matched case-insensitively. Keep the list lower-case. */
const emailIn =
  (...emails: readonly string[]): Rule =>
  (s) =>
    s.email !== undefined && emails.includes(s.email.toLowerCase())

const all =
  (...rules: Rule[]): Rule =>
  (s) =>
    rules.every((rule) => rule(s))

const any =
  (...rules: Rule[]): Rule =>
  (s) =>
    rules.some((rule) => rule(s))

/* ────────────────────────────────────────────────────────────────────────────
 *  The policy
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Customers with early access to agent chat. Ported from `getAgentModeFlag`
 * in `use-feature-flags.ts`.
 *
 * An email allow-list in the bundle is the wrong long-term home for this: it
 * ships customer addresses to every client and needs a deploy to change. It is
 * kept verbatim here so centralising the gate does not also change who has
 * access; moving it behind an org-level entitlement is a follow-up.
 */
const AGENT_MODE_ALLOWED_EMAILS: readonly string[] = [
  'ed@arthospitality.co.uk',
  'liam@arthospitality.co.uk',
  'john@oir-group.com',
  'matthew@sankeys.co.uk',
  'louis@howies.uk.com',
]

/**
 * Organisations piloting the AI voice agent ahead of release. An org id rather
 * than an email so the whole organisation gets it, and so nothing
 * customer-identifying ships in the bundle. Same caveat as the agent-mode list
 * above: a deploy to change it, and an org-level entitlement is the proper
 * home once voice is released.
 */
const VOICE_EARLY_ACCESS_ORGS: readonly string[] = [
  'f0e1147e-52e8-4751-ba01-4107b57e3203', // Sankey's Hospitality
  '15f31234-d4f3-11eb-8ac4-020f63063aab',
  '0312c702-361f-11ea-9472-06a4d6597160',
  'c27b032f-bd39-431f-8c58-ff85aac5a726',
  '0312a687-361f-11ea-9472-06a4d6597160',
  '8718623a-06ac-4eb5-82da-4c02722acc1e',
  'cb2dba04-1a93-11ee-804c-0a58a9feac02',
  'bbc14af7-0cc0-4737-a389-d78116fd32f2', // Pizza Punks
]

/**
 * Users with early access to the Enquiries workspace (zion
 * ENQUIRIES_WORKSPACE_ARD.md D17) while it is built. Email addresses rather
 * than org ids, because access is being handed to named people at a customer,
 * not to whole organisations. Lower-case. Same caveats as the agent-mode list
 * above: addresses ship in the bundle and a deploy is needed to change them;
 * an org-level entitlement is the proper home on release.
 */
const ENQUIRIES_WORKSPACE_EARLY_ACCESS_EMAILS: readonly string[] = [
  // Howies
  'stephanie@howies.uk.com',
  'louis@howies.uk.com',
  'hr@howies.uk.com',
]

/**
 * Organisations with early access to the Enquiries workspace, as opposed to
 * the named people above.
 *
 * Both lists exist because the two are different grants: an address lets one
 * person at a customer in, an org id lets everyone there in. Handing a whole
 * organisation access by listing its staff one at a time means the next
 * joiner silently has not got it.
 */
const ENQUIRIES_WORKSPACE_EARLY_ACCESS_ORGS: readonly string[] = [
  'ad27ac72-b041-48ea-a135-a81b2d335d46',
]

/**
 * Every capability, and the rule that grants it.
 *
 * `Record<Capability, Rule>` is exhaustive by construction: adding a member to
 * the `Capability` union without a rule here is a type error, so a new
 * capability can never silently default to allowed.
 */
export const POLICY: Record<Capability, Rule> = {
  // ── Sections. Role-gated via the ported deny map. ──
  'nav.admin': all(section('admin'), staff),
  'nav.home': section('home'),
  'nav.venues': section('venues'),
  'nav.wi-fi': section('wi-fi'),
  'nav.bookings': section('bookings'),
  'nav.reviews': section('reviews'),
  'nav.marketing': section('marketing'),
  'nav.inbox': section('inbox'),
  'nav.loyalty': section('loyalty'),
  'nav.gift-cards': section('gift-cards'),
  'nav.reports': section('reports'),
  'nav.brand': section('brand'),
  'nav.scanner': section('scanner'),
  // Agent chat has never been role-gated, only staff/allow-list gated.
  'nav.chat': (s) => POLICY['chat.agent-mode'](s),
  // These four sections (Square POS, Email Audit, Marketplace, Hardware) predate
  // `denyByKey` and had no role gating at all, which meant floor staff saw the
  // lot. Deliberately gated ONLY against the operational roles rather than
  // narrowed to admins: nobody who can see them today loses them, and widening a
  // gate later is easier to justify than explaining a section that vanished.
  'nav.org-tools': roleNotIn('front-of-house', 'it-support'),
  // IT Support keeps Hardware — diagnosing kit is the job.
  'nav.hardware': roleNotIn('front-of-house'),
  // Ordering is the sibling of Bookings and inherited Bookings → Menus, which was
  // `bookings.menus` (section + not FOH / booking). Orders and refunds are
  // operational, so on release the section wants role narrowing like the other
  // org tools; the org-level "is ordering on" half lives in the nav ctx.
  //
  // RELEASED 2026-09-12, for the cut-over that moves menus and orders to Mouse
  // for every organisation. It was Stampede only from 2026-09-08 while the two
  // surfaces ran side by side.
  //
  // It has to widen in the SAME release as the flip, not after it. From the
  // flip, Connect's old Zion menu editor redirects here (`LegacyMenusRedirect`,
  // fired by the org-level ordering capability alone, which has no role in it)
  // and every Zion menu write answers 409. A staff-only gate here would then
  // send a customer's own manager to a full-page 403 whose Go back button
  // returns them to the redirect and into the same 403: no menu editor at all,
  // neither the old one nor the new one.
  //
  // Front of house and IT support are excluded for the same reason they are
  // excluded from the other org tools: menus are commercial configuration, and
  // prices and availability are not theirs to change.
  'nav.ordering': roleNotIn('front-of-house', 'it-support'),
  // The product changelog: every role, gated only so that the nav invariant
  // holds and it cannot render for an unresolved subject.
  'nav.whats-new': anyRole,
  // Referrals: every role, same reasoning — the capability exists only so
  // the nav invariant holds, not to restrict who sees it.
  'nav.referrals': anyRole,
  // Customers: every role, same reasoning. Customer Search inside it is for
  // everyone; Know your customer narrows itself with `kyc.view`.
  'nav.customers': anyRole,
  // Support: staff only while the agent, the admin queue and the film library
  // are being built. Covers the nav item AND the routes (`paths.tsx` reads the
  // same capability), so a venue cannot reach a half-finished surface by URL.
  // To release: swap `staff` for `anyRole` and drop the nav badge.
  /**
   * EVERYONE, as of this change.
   *
   * It was `staff` while the surface was being built, which meant a
   * customer pressing cmd+/ got nothing at all: the dock rendered
   * nothing, the chord was never bound, and the mobile pill's hold armed
   * nothing. Three places, all reading this one line, so they could not
   * drift apart.
   *
   * What sits behind it is read-mostly. It can change a venue's own
   * floor plan and booking rules but not delete anything, it cannot
   * touch a live booking or an order, every lookup is confined to the
   * caller's organisation IN CODE rather than by instruction, and
   * `actingAsCustomer` suppresses every staff privilege a tool would
   * otherwise grant on the strength of an email address.
   *
   * What is NOT yet in place is a human in front of its writes.
   * `InternalAgentActionProposal` is the primitive for that and is not
   * wired to this agent, so the floor-plan tools act immediately. That is
   * the thing to watch, and the reason to leave it as one line somebody
   * can put back.
   */
  'nav.support': () => true,

  // ── Internal ──
  'internal.staff': staff,
  // Ported from `pages/admin/layout.tsx` — the ONLY gate that also wants 2FA.
  'internal.admin-console': (s) => s.isSuperAdmin && s.has2FA,
  // Points catch-up writes points across a whole scheme, so superadmin only.
  'loyalty.points-catchup': (s) => s.isSuperAdmin,
  'internal.engineering': staff,
  'internal.boost': staff,
  'internal.debug': staff,
  'internal.new-ui-toggle': staff,

  // ── Plan. `*-plus` implies its base tier (ported fallthrough). ──
  'plan.bookings': plan('bookings'),
  'plan.contacts': plan('contacts'),
  'plan.gift-cards': planOrPlus('gift_cards', 'gift_cards_plus'),
  'plan.gift-cards-plus': plan('gift_cards_plus'),
  'plan.loyalty': plan('loyalty'),
  'plan.marketing': planOrPlus('marketing', 'marketing_plus'),
  'plan.marketing-plus': plan('marketing_plus'),
  'plan.reviews': plan('reviews'),
  'plan.wifi': planOrPlus('wifi', 'wifi_plus'),
  'plan.wifi-plus': plan('wifi_plus'),

  // ── Org / account administration ──
  // The whole `account/organisation/*` subtree. Admin and above, plus the org
  // owner: an owner may hold a junior role, and locking them out of their own
  // billing would be a worse bug than the one this fixes. Same reasoning as
  // `org.team.invite`, which sits inside this section.
  'org.account': any(atLeast('admin'), orgOwner),
  // Ported from `canEditSettings` in `venues/[serial]/index.tsx`.
  'org.settings.edit': roleIn('super', 'reseller', 'admin'),
  // The `|| user.uid === owner_id` escape hatch from `team.tsx:280`: an owner who
  // holds no admin role still runs their own org. Porting `atLeast('admin')`
  // alone would have locked those owners out of inviting anyone.
  'org.team.invite': any(atLeast('admin'), orgOwner),
  'org.billing.view': atLeast('admin'),
  // Ported from the operational-role checks in `profile-nav.tsx`.
  'org.venues.add': (s) => Boolean(s.role) && !isOperationalRole(s),
  'org.switch': (s) => Boolean(s.role) && !isOperationalRole(s),
  'venue.settings.edit': roleIn('super', 'reseller', 'admin'),

  // ── Marketing ──
  'marketing.ai-email-templates': staff,
  'marketing.audiences.classic': provider('morpheus'),
  'marketing.audiences.new': provider('morpheus_simple_segments'),
  'marketing.audiences.delete-data': staff,
  // Ported from `useHasUserRole(AvailableRoles.Admin)` in the approval flows.
  'marketing.campaigns.approve': atLeast('admin'),
  'marketing.workflow-logs': staff,

  // ── Bookings ──
  //
  // Depth within the section, ported from the `fohOnlyRoutes` /
  // `bookingManagerRoutes` allow-lists in `venues/[serial]/bookings/index.tsx`.
  // Until now those only filtered the tiles on the bookings landing page: the
  // sidebar showed FOH every child, settings and menus included.
  //
  // ONE deliberate difference from the old arrays: `fohOnlyRoutes` listed only
  // day-planner / list-view / floorplan, but FOH is meant to work enquiries too,
  // so `bookings.enquiries` is open to them here. Everything else is a faithful
  // port. The old arrays should be deleted in favour of these capabilities.
  //
  // Net effect — FOH: views + enquiries. Bookings Manager: those, plus deposits
  // and reports. Everyone else with Bookings access: the lot.
  'bookings.views': section('bookings'),
  'bookings.enquiries': section('bookings'),
  'bookings.deposits': all(section('bookings'), roleNotIn('front-of-house')),
  // Same rule as `bookings.deposits`, on purpose: taking the money and giving it
  // back are one job. Keep the two in step if either ever moves.
  'bookings.refund': all(section('bookings'), roleNotIn('front-of-house')),
  'bookings.reports': all(section('bookings'), roleNotIn('front-of-house')),
  'bookings.outstanding-payments': all(
    section('bookings'),
    roleNotIn('front-of-house', 'booking')
  ),
  'bookings.layout': all(
    section('bookings'),
    roleNotIn('front-of-house', 'booking')
  ),
  'bookings.menus': all(
    section('bookings'),
    roleNotIn('front-of-house', 'booking')
  ),
  'bookings.settings': all(
    section('bookings'),
    roleNotIn('front-of-house', 'booking')
  ),
  // Booking guardrails / "Restricted mode". Ported from FOUR copies of the same
  // array: `BookingFormOptimized.tsx:143` (`canOverrideRole`) and `:310`
  // (`isAdmin` — the same list under a second name in the same file),
  // `WalkInForm.tsx:78`, and `use-booking-form-host-data.ts:544`. The dp3 copy
  // even cites line numbers in the fast form that have since moved, which is
  // what four copies of a rule looks like as it rots.
  //
  // `roleIn`, NOT `atLeast('admin')`: `reseller` is deliberately absent because
  // it is absent today. That looks unintended — reseller outranks admin
  // everywhere else in `SATISFIED_BY`, and `venue.settings.edit` does include it
  // — but it is shipped behaviour and changing it here would be a silent
  // permission grant. Decide it as its own change.
  'bookings.override-guardrails': roleIn('admin', 'super', 'moderator', 'gm'),
  // Wider by exactly one role: reservations staff need to take card-less
  // bookings even with guardrails on.
  'bookings.override-deposit': any(
    roleIn('admin', 'super', 'moderator', 'gm'),
    roleIn('booking')
  ),
  // Ported from `useHasUserRole(AvailableRoles.GM)` in floorplan-3.
  'bookings.floorplan.edit': atLeast('gm'),
  'bookings.floorplan.mobile-layout': staff,
  'bookings.waitlist': staff,
  'bookings.day-planner.new-grid': staff,
  'bookings.list-view-3': staff,
  'bookings.venue-music.demo': staff,
  // Pilot: staff, or a piloting organisation whose user also holds
  // `bookings.settings` (provisioning a phone number and tuning the agent's
  // voice are settings-level actions, so floor staff do not see a page their
  // admins cannot). Widen to `bookings.settings` outright to release.
  'bookings.voice': any(
    staff,
    all(orgIn(...VOICE_EARLY_ACCESS_ORGS), (s) =>
      POLICY['bookings.settings'](s)
    )
  ),
  // Early access: staff, a named user, or anyone at an early-access org -
  // and in every case only if they also hold `bookings.enquiries`, because
  // the workspace IS the enquiries app and nobody should see it who cannot
  // see enquiries. Widen to `bookings.enquiries` outright to release it.
  'bookings.enquiries-workspace': any(
    staff,
    all(emailIn(...ENQUIRIES_WORKSPACE_EARLY_ACCESS_EMAILS), (s) =>
      POLICY['bookings.enquiries'](s)
    ),
    all(orgIn(...ENQUIRIES_WORKSPACE_EARLY_ACCESS_ORGS), (s) =>
      POLICY['bookings.enquiries'](s)
    )
  ),
  // Released (October 2026): anyone who can see bookings Settings. Kept as its
  // own capability so the nav and routes read as "ticketed events", not as a
  // generic settings check.
  'bookings.ticketed-events': (s) => POLICY['bookings.settings'](s),

  // ── Reviews ──
  // Ported from `ORG_LEVEL_ROLES` in `venues/[serial]/reviews/index.tsx:59`,
  // which is the app's only role list keyed on `name` rather than `key`. Flat,
  // not a hierarchy: GM and Bookings Manager are excluded on purpose — a
  // venue-level role gets its venue, not the org roll-up.
  'reviews.org-level': roleIn(
    'super',
    'reseller',
    'admin',
    'moderator',
    'reports',
    'marketeer'
  ),
  'reviews.ai-analytics': staff,

  // ── Reports ──
  // Full Bhuna's revenue, item and basket figures all come from Square, so
  // without an integration the report is a hollow shell. Better not offered.
  'reports.full-bhuna': (s) => s.hasSquare,

  // ── Customers ──
  // Know your customer reads till orders (times, spend, baskets, modifiers),
  // which only exist with a Square integration. Same rule as Full Bhuna.
  'kyc.view': (s) => s.hasSquare,

  // ── Wi-Fi. Mutually exclusive by network type. ──
  'wifi.portal-payments': (s) => s.isPaidVenue,
  'wifi.surveys': (s) => !s.isPaidVenue,

  // ── Brand ──
  'brand.kit-generation': staff,

  // ── Chat ──
  // Staff, plus a named customer allow-list. Ported from `getAgentModeFlag`.
  'chat.agent-mode': any(staff, (s) =>
    AGENT_MODE_ALLOWED_EMAILS.includes((s.email ?? '').toLowerCase())
  ),
}

/**
 * The one question. Pure — same subject and capability, same answer, always.
 *
 * Prefer `useAccess().can(...)` in components; call this directly from pure
 * code (nav visibility resolvers, tests, route guards).
 */
export function can(subject: AccessSubject, capability: Capability): boolean {
  return POLICY[capability](subject)
}

/** Convenience inverse, for readability at call sites that guard early returns. */
export function cannot(
  subject: AccessSubject,
  capability: Capability
): boolean {
  return !can(subject, capability)
}

/**
 * Every capability this subject holds. For debugging and for the staff-facing
 * access inspector; not a hot path.
 */
export function grantedCapabilities(subject: AccessSubject): Capability[] {
  const keys = Object.keys(POLICY) as Capability[]
  return keys.filter((capability) => POLICY[capability](subject))
}
